import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Search, Plus, Mail, Building2, MapPin, Trash2, RotateCcw, AlertTriangle, ShieldAlert, Eye } from 'lucide-react';

const ROLE_LABELS = {
    super_admin: 'Super Admin', company_admin: 'Company Admin', learning_manager: 'Learning Manager',
    department_manager: 'Dept Manager', hr_admin: 'HR Admin', trainer: 'Trainer',
    compliance_officer: 'Compliance', regional_manager: 'Regional Mgr', branch_manager: 'Branch Mgr',
    trainee: 'Trainee', leadership_viewer: 'Leadership',
};

const ROLE_COLORS = {
    company_admin: 'bg-brand-blue/10 text-brand-blue',
    trainer: 'bg-violet-100 text-violet-700',
    trainee: 'bg-emerald-100 text-emerald-700',
};

const SUPER_ROLES = ['super_admin', 'company_admin'];

export default function AdminUsers() {
    const { user: me } = useAuth();
    const navigate = useNavigate();
    const isSuper = !!me && SUPER_ROLES.includes(me.role);
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState('');
    const [role, setRole] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [confirmBusy, setConfirmBusy] = useState(false);

    const load = () => {
        const params = {};
        if (q) params.q = q;
        if (role) params.role = role;
        api.get('/admin/users', { params }).then((r) => setUsers(r.data));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, role]);

    const updateGroup = async (userId, group) => {
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, game_group: group } : u)));
        try { await api.patch(`/admin/users/${userId}`, { game_group: group }); } catch { load(); }
    };

    const runConfirm = async () => {
        if (!confirm) return;
        setConfirmBusy(true);
        try { await confirm.onConfirm(); } finally { setConfirmBusy(false); setConfirm(null); }
    };

    const askReset = (u) => setConfirm({
        title: `Reset ${u.full_name}'s progress?`,
        message: 'This clears their course progress, quiz & game data, certificates and points. The account stays. This cannot be undone.',
        confirmLabel: 'Reset progress',
        testid: 'confirm-reset-user',
        onConfirm: async () => {
            try { await api.post(`/admin/users/${u.id}/reset`); toast.success(`${u.full_name}'s progress reset`); load(); }
            catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
        },
    });
    const askDelete = (u) => setConfirm({
        title: `Delete ${u.full_name}?`,
        message: 'This permanently removes the user AND all their data (progress, quizzes, certificates, points). This cannot be undone.',
        confirmLabel: 'Delete user',
        testid: 'confirm-delete-user',
        onConfirm: async () => {
            try { await api.delete(`/admin/users/${u.id}`); toast.success(`${u.full_name} deleted`); load(); }
            catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
        },
    });

    return (
        <div data-testid="admin-users-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display font-extrabold text-3xl text-slate-900">Users & Roles</h1>
                    <p className="text-sm text-slate-500 mt-1">Manage employees, dealers, trainers and admins.</p>
                </div>
                <button onClick={() => setShowCreate(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-blue text-white font-semibold hover:bg-brand-blueDark"
                    data-testid="users-create-btn">
                    <Plus className="h-4 w-4" /> Add User
                </button>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-col sm:flex-row gap-2 mb-5">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…"
                        className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm" data-testid="users-search-input" />
                </div>
                <select value={role} onChange={(e) => setRole(e.target.value)}
                    className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm" data-testid="users-role-filter">
                    <option value="">All roles</option>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left py-3 px-4">Name</th>
                            <th className="text-left py-3 px-4 hidden md:table-cell">Email</th>
                            <th className="text-left py-3 px-4">Role</th>
                            <th className="text-left py-3 px-4">Group</th>
                            <th className="text-left py-3 px-4 hidden lg:table-cell">Department</th>
                            <th className="text-left py-3 px-4 hidden lg:table-cell">Region</th>
                            <th className="text-right py-3 px-4">Points</th>
                            <th className="text-right py-3 px-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {users.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50">
                                <td className="py-3 px-4 font-semibold text-slate-900">{u.full_name}</td>
                                <td className="py-3 px-4 text-slate-600 hidden md:table-cell"><span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{u.email}</span></td>
                                <td className="py-3 px-4">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-700'}`}>
                                        {ROLE_LABELS[u.role] || u.role}
                                    </span>
                                </td>
                                <td className="py-3 px-4">
                                    <select value={u.game_group || 'A'} onChange={(e) => updateGroup(u.id, e.target.value)}
                                        className="px-2 py-1 rounded-lg border border-slate-200 text-xs font-bold bg-white" data-testid={`user-group-${u.id}`}>
                                        {['A', 'B', 'C', 'D'].map((g) => <option key={g} value={g}>Group {g}</option>)}
                                    </select>
                                </td>
                                <td className="py-3 px-4 text-slate-600 hidden lg:table-cell"><span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{u.department || '—'}</span></td>
                                <td className="py-3 px-4 text-slate-600 hidden lg:table-cell"><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{u.region || '—'}</span></td>
                                <td className="py-3 px-4 text-right font-display font-extrabold text-brand-blue">{u.points || 0}</td>
                                <td className="py-3 px-4">
                                    <div className="flex items-center justify-end gap-1.5">
                                        <button onClick={() => navigate(`/admin/learners/${u.id}`)} title="View learner detail"
                                            className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100" data-testid={`user-view-${u.id}`}>
                                            <Eye className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => askReset(u)} title="Reset this user's progress"
                                            className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50" data-testid={`user-reset-${u.id}`}>
                                            <RotateCcw className="h-4 w-4" />
                                        </button>
                                        {isSuper && u.id !== me.id && (
                                            <button onClick={() => askDelete(u)} title="Delete this user"
                                                className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50" data-testid={`user-delete-${u.id}`}>
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!users.length && (
                            <tr><td colSpan={8} className="py-10 text-center text-slate-500 text-sm">No users match the filter.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {isSuper && <DangerZone onDone={load} />}

            {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
            {confirm && (
                <ConfirmDialog {...confirm} busy={confirmBusy} onConfirm={runConfirm} onClose={() => setConfirm(null)} />
            )}
        </div>
    );
}

const WIPE_CATS = [
    { key: 'course_progress', label: 'Course progress & completion' },
    { key: 'quiz_game', label: 'Quiz & game data (Pulse Power/Crush, attempts & scores)' },
    { key: 'certificates', label: 'Certificates earned' },
    { key: 'points_badges', label: 'Points & badges (reset every learner to 0)' },
    { key: 'loyalty_logs', label: 'Loyalty logs (activity / point-credit / token history)' },
    { key: 'notifications', label: 'Notifications & reward redemptions' },
];

function DangerZone({ onDone }) {
    const [sel, setSel] = useState({});
    const [delTrainees, setDelTrainees] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const chosen = WIPE_CATS.filter((c) => sel[c.key]).map((c) => c.key);
    const canRun = (chosen.length > 0 || delTrainees) && confirmText === 'WIPE';

    const run = async () => {
        setBusy(true);
        try {
            const r = await api.post('/admin/danger/wipe', { categories: chosen, delete_trainees: delTrainees, confirm: 'WIPE' });
            const parts = Object.entries(r.data.results || {}).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`);
            if (r.data.deleted_trainees) parts.push(`trainees deleted: ${r.data.deleted_trainees}`);
            toast.success('Go-live reset complete' + (parts.length ? ` — ${parts.join(', ')}` : ''));
            setSel({}); setDelTrainees(false); setConfirmText('');
            onDone && onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
        finally { setBusy(false); setConfirm(false); }
    };

    return (
        <div className="mt-8 rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-5" data-testid="danger-zone">
            <div className="flex items-center gap-2 text-rose-700">
                <ShieldAlert className="h-5 w-5" />
                <h3 className="font-display font-bold text-lg">Danger Zone — Go-Live Reset</h3>
            </div>
            <p className="text-sm text-rose-700/80 mt-1">
                Wipe UAT user data before going live. This <b>permanently</b> clears the selected data for <b>all</b> users.
                It never touches your content (courses, quizzes, games, stories, badge setup) or Admin/Trainer accounts.
            </p>

            <div className="grid sm:grid-cols-2 gap-2 mt-4">
                {WIPE_CATS.map((c) => (
                    <label key={c.key} className="flex items-start gap-2.5 bg-white rounded-xl border border-rose-100 px-3 py-2.5 cursor-pointer">
                        <input type="checkbox" checked={!!sel[c.key]} onChange={(e) => setSel({ ...sel, [c.key]: e.target.checked })}
                            className="mt-0.5 h-4 w-4 accent-rose-600" data-testid={`wipe-cat-${c.key}`} />
                        <span className="text-sm font-medium text-slate-700">{c.label}</span>
                    </label>
                ))}
            </div>

            <label className="flex items-start gap-2.5 mt-3 bg-white rounded-xl border-2 border-rose-200 px-3 py-2.5 cursor-pointer">
                <input type="checkbox" checked={delTrainees} onChange={(e) => setDelTrainees(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-rose-600" data-testid="wipe-delete-trainees" />
                <span className="text-sm font-semibold text-rose-700">Also DELETE all Trainee accounts (Admins & Trainers are kept)</span>
            </label>

            <div className="flex flex-col sm:flex-row sm:items-end gap-3 mt-4">
                <label className="block flex-1">
                    <span className="text-xs font-semibold text-slate-700">Type <b>WIPE</b> to enable the button</span>
                    <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="WIPE"
                        className="mt-1 w-full px-3 py-2.5 rounded-xl border border-rose-200 text-sm font-mono" data-testid="wipe-confirm-input" />
                </label>
                <button disabled={!canRun || busy} onClick={() => setConfirm(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 text-white font-semibold hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="wipe-execute-btn">
                    <Trash2 className="h-4 w-4" /> Run go-live reset
                </button>
            </div>

            {confirm && (
                <ConfirmDialog
                    title="Run the go-live reset?"
                    message={`This will permanently clear: ${chosen.map((k) => k.replace(/_/g, ' ')).join(', ') || '(no categories)'}${delTrainees ? ' and DELETE all Trainee accounts' : ''}. This cannot be undone.`}
                    confirmLabel="Yes, wipe now" busy={busy} testid="confirm-wipe"
                    onConfirm={run} onClose={() => setConfirm(false)} />
            )}
        </div>
    );
}

function ConfirmDialog({ title, message, confirmLabel = 'Confirm', busy, onConfirm, onClose, testid = 'confirm-dialog' }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
            <div className="bg-white rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-rose-100 text-rose-600 shrink-0"><AlertTriangle className="h-5 w-5" /></span>
                    <div>
                        <h3 className="font-display font-bold text-lg text-slate-900">{title}</h3>
                        <p className="text-sm text-slate-600 mt-1">{message}</p>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="confirm-cancel-btn">Cancel</button>
                    <button onClick={onConfirm} disabled={busy}
                        className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60" data-testid="confirm-ok-btn">
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CreateUserModal({ onClose, onCreated }) {
    const [f, setF] = useState({ email: '', password: 'Temp@1234', full_name: '', role: 'trainee', department: '', region: '' });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const save = async () => {
        setBusy(true); setErr('');
        try {
            await api.post('/admin/users', f);
            onCreated();
        } catch (e) { setErr(e?.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="create-user-modal">
            <div className="bg-white rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-display font-bold text-xl text-slate-900">Add a user</h3>
                {err && <div className="mt-3 text-sm text-red-600">{err}</div>}
                <div className="grid gap-3 mt-4">
                    <Inp label="Full name" v={f.full_name} on={(v) => setF({ ...f, full_name: v })} t="create-user-name" />
                    <Inp label="Email" v={f.email} on={(v) => setF({ ...f, email: v })} t="create-user-email" />
                    <Inp label="Temporary password" v={f.password} on={(v) => setF({ ...f, password: v })} t="create-user-password" />
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700">Role</span>
                        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}
                            className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" data-testid="create-user-role">
                            {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </label>
                    <Inp label="Department" v={f.department} on={(v) => setF({ ...f, department: v })} t="create-user-department" />
                    <Inp label="Region" v={f.region} on={(v) => setF({ ...f, region: v })} t="create-user-region" />
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="create-user-cancel">Cancel</button>
                    <button onClick={save} disabled={busy} className="px-4 py-2 rounded-xl bg-brand-blue text-white text-sm font-semibold disabled:opacity-60" data-testid="create-user-save">
                        {busy ? 'Saving…' : 'Create User'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Inp({ label, v, on, t }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-slate-700">{label}</span>
            <input value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" data-testid={t} />
        </label>
    );
}
